/*
 * DatapointPicker — Auswahl EINES Datenpunkts fuer den HMI-Editor. Ergebnis
 * nach aussen ist ausschliesslich der Binding-Key aus `formatBindingKey`,
 * nie eine Leaf-Id und nie ein zusammengebauter String.
 *
 * Warum ein eigener Waehler und nicht LeafPicker erweitert:
 *
 *   - `components/LeafPicker.tsx` kann fast das Richtige (Modal, Einzelwahl,
 *     Suche ueber Symbol, Name, Pfad, Einheit), aber es rendert JEDE Zeile:
 *     bei 4518 Punkten an einem Regler sind das 4518 Knoten im Baum, und die
 *     Seite ruckelt schon beim Tippen. Ihm die Virtualisierung nachzuruesten
 *     hiesse, seinen einzigen heutigen Aufrufer (WidgetConfigEditor) ohne
 *     jeden Test umzubauen. Es bleibt deshalb unangetastet.
 *   - `components/charts/MultiLeafPicker.tsx` hat genau das, was hier fehlt
 *     (Virtualisierung, gerankter Filter, Geraete-Umschalter), aber als
 *     Mehrfachauswahl mit Ankreuzfeldern und als eingebettetes Panel. Aus ihm
 *     einen Einzelwahl-Dialog zu machen haette die Chart-Seite umgebaut.
 *
 * Also der dritte Weg: die beiden Teile, die niemandem gehoeren duerfen, sind
 * aus MultiLeafPicker herausgezogen (`datapointRows.ts` fuer Zeilen, Baum,
 * Filter und Rang; `useVirtualRows.ts` fuer das Fenster) und werden jetzt von
 * beiden benutzt. Dieser Waehler ist nur noch Oberflaeche darauf. Kopiert
 * wurde nichts.
 *
 * Zur Last: nicht begrenzt, sondern virtualisiert. Eine Kappung waere hier
 * das falsche Mittel, weil der Baum zum Stoebern da ist und ein abgeschnittener
 * Baum luegt. Gerendert werden nur die Zeilen im Blickfeld plus Ueberhang,
 * unabhaengig davon, ob 40 oder 4518 Punkte passen. Der Baum kostet zusaetzlich
 * nur die aufgeklappten Aeste (siehe `flattenVisibleTreeRows`).
 *
 * Suche ist der Hauptweg, der Baum der Nebenweg: das Suchfeld hat den Fokus,
 * und solange etwas darin steht, zeigt die Liste gerankte Treffer statt des
 * Baums.
 */

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api } from '../api/client';
import type { PlantSchemaResponse } from '../api/types';
import { useAsync, type AsyncState } from '../hooks/useAsync';
import { formatBindingKey, parseBindingKey } from '../hmi/bindingKey';
import {
  VALUE_TYPE_LABELS,
  buildDatapointGroups,
  classifyValueType,
  defaultExpandedGroupIds,
  describeFilter,
  flattenDatapointRows,
  flattenVisibleTreeRows,
  indexRowsByLeafId,
  isWritableLeaf,
  makeLeafPredicate,
  pathToLeaf,
  rankFilterDatapointRows,
  type DatapointFilter,
  type DatapointRow,
} from './datapointRows';
import { useVirtualRows } from './useVirtualRows';
import './DatapointPicker.css';

/** Zeilenhoehen. Fest, weil die Virtualisierung Zeilen nach Index positioniert;
 *  je Ansicht eine Hoehe, innerhalb einer Ansicht alle gleich. */
const RESULT_ROW_HEIGHT = 46;
const TREE_ROW_HEIGHT = 28;
const OVERSCAN_ROWS = 6;

export interface DatapointPickerProps {
  plantId: string;
  /** Aktuell gebundener Binding-Key, nur zum Hervorheben. */
  value?: string;
  /** Einschraenkung der waehlbaren Punkte. */
  filter?: DatapointFilter;
  /** Ueberschrift, etwa der Name des Eigenschaftsfelds. */
  title?: string;
  /** Liefert ausschliesslich den kanonischen Binding-Key. */
  onPick(bindingKey: string): void;
  onClose(): void;
}

export function DatapointPicker({
  plantId, value, filter, title, onPick, onClose,
}: DatapointPickerProps) {
  const state = usePlantDatapoints(plantId);

  const [query, setQuery] = useState('');
  const [deviceFilter, setDeviceFilter] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set<string>());
  /** Wurde der Baum fuer diese Anlage schon einmal vorbereitet? Ohne diesen
   *  Riegel wuerde der Effekt unten jedes Auf- und Zuklappen ueberschreiben. */
  const [primedFor, setPrimedFor] = useState<string | null>(null);

  const currentLeafId = useMemo(() => parseBindingKey(value)?.leafId ?? null, [value]);
  const predicate = useMemo(() => makeLeafPredicate(filter), [filter]);

  const data = state.status === 'ok' ? state.data : null;

  // Gefilterter Baum und gefilterte Flachliste. Beides einmal je Schema und
  // Filter, nicht je Tastendruck.
  const groups = useMemo(
    () => (data ? buildDatapointGroups(data.schema, predicate) : []),
    [data, predicate],
  );
  const rows = useMemo(
    () => (data ? (predicate ? data.rows.filter((r) => predicate(r.leaf)) : data.rows) : []),
    [data, predicate],
  );

  // Beim Oeffnen: Geraete auf, und der Ast zum bereits gebundenen Punkt auf,
  // damit der Autor sieht, wo er steht, statt vor einem zugeklappten Baum.
  useEffect(() => {
    if (!data || primedFor === data.schema.plantId) return;
    const next = new Set(defaultExpandedGroupIds(groups));
    if (currentLeafId) for (const id of pathToLeaf(groups, currentLeafId)) next.add(id);
    setExpanded(next);
    setPrimedFor(data.schema.plantId);
  }, [data, groups, currentLeafId, primedFor]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const searching = query.trim().length > 0;

  const results = useMemo(
    () => (searching ? rankFilterDatapointRows(rows, query, deviceFilter) : []),
    [searching, rows, query, deviceFilter],
  );
  const treeRows = useMemo(
    () => (searching
      ? []
      : flattenVisibleTreeRows(
        deviceFilter ? groups.filter((g) => g.deviceId === deviceFilter) : groups,
        expanded,
      )),
    [searching, groups, expanded, deviceFilter],
  );

  const rowHeight = searching ? RESULT_ROW_HEIGHT : TREE_ROW_HEIGHT;
  const count = searching ? results.length : treeRows.length;
  const { scrollRef, startIndex, endIndex, totalHeight, scrollToTop } =
    useVirtualRows(count, rowHeight, OVERSCAN_ROWS);

  // Tauscht sich die Liste unter dem Finger aus, steht man sonst mitten im
  // Nichts und haelt die leere Flaeche fuer "keine Treffer".
  useEffect(() => { scrollToTop(); }, [query, deviceFilter, searching, scrollToTop]);

  const pick = (row: DatapointRow) => {
    onPick(formatBindingKey(plantId, row.leaf.id));
    onClose();
  };

  const toggleGroup = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filterText = describeFilter(filter);
  const devices = data ? data.schema.devices : [];
  // Zahl im Kopf: im Baum die Treffer der sichtbaren Regler, nicht die der
  // ganzen Anlage — sonst widerspricht sie dem, was darunter steht.
  const browseCount = useMemo(
    () => (deviceFilter ? groups.filter((g) => g.deviceId === deviceFilter) : groups)
      .reduce((sum, g) => sum + g.matchCount, 0),
    [groups, deviceFilter],
  );

  // Fokus aufs Suchfeld, gleiches Muster wie in `components/LeafPicker.tsx`:
  // wer den Waehler oeffnet, hat meistens schon ein Wort im Kopf.
  const searchRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => { searchRef.current?.focus(); }, []);

  return createPortal(
    <div class="hv-dp-picker" role="dialog" aria-modal="true" aria-label="Datenpunkt wählen">
      <div class="hv-dp-picker__backdrop" onClick={onClose} />
      <div class="hv-dp-picker__panel">
        <header class="hv-dp-picker__header">
          <div class="hv-dp-picker__headings">
            <h2 class="hv-dp-picker__title">{title ?? 'Datenpunkt wählen'}</h2>
            <p class="hv-dp-picker__subtitle muted">
              {state.status === 'ok' ? state.data.schema.name : 'Anlage wird geladen…'}
              {filterText && <> · {filterText}</>}
            </p>
          </div>
          <button
            type="button"
            class="hv-dp-picker__close"
            onClick={onClose}
            aria-label="Schließen"
            title="Schließen"
          >
            ×
          </button>
        </header>

        <div class="hv-dp-picker__search-row">
          <input
            ref={searchRef}
            type="search"
            class="hv-dp-picker__search"
            placeholder="Suchen: Symbol, Name, Pfad, Einheit…"
            value={query}
            onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && results.length > 0) {
                e.preventDefault();
                pick(results[0]);
              }
            }}
            aria-label="Datenpunkte suchen"
          />
          <span class="hv-dp-picker__count muted">
            {searching
              ? `${results.length} Treffer`
              : `${browseCount} Datenpunkt${browseCount === 1 ? '' : 'e'}`}
          </span>
        </div>

        {devices.length > 1 && (
          <div class="hv-dp-picker__devices" role="group" aria-label="Regler-Filter">
            <button
              type="button"
              class={`hv-dp-picker__chip${deviceFilter === null ? ' hv-dp-picker__chip--active' : ''}`}
              onClick={() => setDeviceFilter(null)}
            >
              Alle Regler
            </button>
            {devices.map((d) => (
              <button
                key={d.id}
                type="button"
                class={`hv-dp-picker__chip${deviceFilter === d.id ? ' hv-dp-picker__chip--active' : ''}`}
                onClick={() => setDeviceFilter(d.id)}
                title={d.protocol ? `${d.name || d.symbol} · ${d.protocol}` : (d.name || d.symbol)}
              >
                {d.name || d.symbol}
              </button>
            ))}
          </div>
        )}

        <div class="hv-dp-picker__list" ref={scrollRef} role="listbox" tabIndex={-1}>
          {state.status === 'loading' && <p class="hv-dp-picker__note muted">Schema wird geladen…</p>}
          {state.status === 'error' && (
            <p class="hv-dp-picker__note hv-dp-picker__note--error">
              Anlagen-Schema konnte nicht geladen werden.
            </p>
          )}
          {state.status === 'ok' && rows.length === 0 && (
            <p class="hv-dp-picker__note muted">
              {filterText
                ? `Diese Anlage hat keinen Datenpunkt, der passt (${filterText}).`
                : 'Diese Anlage enthält keine Datenpunkte.'}
            </p>
          )}
          {state.status === 'ok' && rows.length > 0 && count === 0 && (
            <p class="hv-dp-picker__note muted">Keine Treffer für „{query}".</p>
          )}

          <div class="hv-dp-picker__spacer" style={{ height: `${totalHeight}px` }}>
            {searching
              ? results.slice(startIndex, endIndex).map((row, i) => (
                <ResultRow
                  key={row.leaf.id}
                  row={row}
                  top={(startIndex + i) * RESULT_ROW_HEIGHT}
                  selected={row.leaf.id.toLowerCase() === currentLeafId}
                  onPick={pick}
                />
              ))
              : treeRows.slice(startIndex, endIndex).map((entry, i) => {
                const top = (startIndex + i) * TREE_ROW_HEIGHT;
                if (entry.kind === 'group') {
                  return (
                    <button
                      key={`g:${entry.group.id}`}
                      type="button"
                      class={`hv-dp-picker__group hv-dp-picker__group--${entry.group.kind}`}
                      style={{ top: `${top}px`, paddingLeft: `${8 + entry.depth * 14}px` }}
                      onClick={() => toggleGroup(entry.group.id)}
                      aria-expanded={entry.expanded}
                      title={entry.group.path || entry.group.deviceSymbol}
                    >
                      <span class="hv-dp-picker__twisty">{entry.expanded ? '▾' : '▸'}</span>
                      <span class="hv-dp-picker__group-label">{entry.group.label}</span>
                      <span class="hv-dp-picker__group-count muted">{entry.group.matchCount}</span>
                    </button>
                  );
                }
                return (
                  <TreeLeafRow
                    key={`l:${entry.row.leaf.id}`}
                    row={entry.row}
                    depth={entry.depth}
                    top={top}
                    selected={entry.row.leaf.id.toLowerCase() === currentLeafId}
                    onPick={pick}
                  />
                );
              })}
          </div>
        </div>

        <footer class="hv-dp-picker__footer muted">
          Suchen ist der schnelle Weg; der Baum ist zum Stöbern. Eingabetaste wählt den
          ersten Treffer.
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Zeilen
// ---------------------------------------------------------------------------

interface RowProps {
  row: DatapointRow;
  top: number;
  selected: boolean;
  onPick(row: DatapointRow): void;
}

/** Trefferzeile der Suche. Zeigt alles, was zwei aehnliche Punkte
 *  unterscheidet: Anzeigename, Symbol, Pfad, Werttyp, Einheit. */
function ResultRow({ row, top, selected, onPick }: RowProps) {
  const leaf = row.leaf;
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      class={`hv-dp-picker__row hv-dp-picker__row--result${selected ? ' hv-dp-picker__row--selected' : ''}`}
      style={{ top: `${top}px`, height: `${RESULT_ROW_HEIGHT}px` }}
      onClick={() => onPick(row)}
      title={`${row.deviceSymbol} · ${row.path} · ${leaf.symbol}`}
    >
      <span class="hv-dp-picker__row-line">
        <span class="hv-dp-picker__row-name">{leaf.displayName || leaf.symbol}</span>
        {leaf.displayName && <span class="hv-dp-picker__row-symbol mono muted">{leaf.symbol}</span>}
        <span class="hv-dp-picker__row-tail">
          <TypeBadge row={row} />
          {leaf.unit && <span class="hv-dp-picker__row-unit muted">{leaf.unit}</span>}
        </span>
      </span>
      <span class="hv-dp-picker__row-path muted">{row.deviceSymbol} › {row.path}</span>
    </button>
  );
}

/** Blattzeile im Baum. Der Pfad steckt in der Einrueckung, deshalb einzeilig;
 *  fuer den Zweifelsfall steht er im Titel. */
function TreeLeafRow({ row, depth, top, selected, onPick }: RowProps & { depth: number }) {
  const leaf = row.leaf;
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      class={`hv-dp-picker__row hv-dp-picker__row--tree${selected ? ' hv-dp-picker__row--selected' : ''}`}
      style={{
        top: `${top}px`,
        height: `${TREE_ROW_HEIGHT}px`,
        paddingLeft: `${8 + depth * 14}px`,
      }}
      onClick={() => onPick(row)}
      title={`${row.deviceSymbol} › ${row.path} · ${leaf.symbol}`}
    >
      <span class="hv-dp-picker__row-name">{leaf.displayName || leaf.symbol}</span>
      <span class="hv-dp-picker__row-symbol mono muted">{leaf.symbol}</span>
      <span class="hv-dp-picker__row-tail">
        <TypeBadge row={row} />
        {leaf.unit && <span class="hv-dp-picker__row-unit muted">{leaf.unit}</span>}
      </span>
    </button>
  );
}

/** Werttyp und, wenn zutreffend, Schreibbarkeit. Der Werttyp steht in der
 *  Editor-Sprache da (Zahl statt Float), weil der Autor Widgets waehlt und
 *  keine Datentypen. Der Rohtyp steht im Titel. */
function TypeBadge({ row }: { row: DatapointRow }) {
  const kind = classifyValueType(row.leaf);
  const label = kind ? VALUE_TYPE_LABELS[kind] : row.leaf.valueType;
  return (
    <>
      <span class="hv-dp-picker__badge" title={`Werttyp ${row.leaf.valueType}`}>{label}</span>
      {isWritableLeaf(row.leaf) && (
        <span class="hv-dp-picker__badge hv-dp-picker__badge--write" title="Beschreibbar">W</span>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Schema-Beschaffung
// ---------------------------------------------------------------------------

/** Anlagenschema plus die daraus abgeleiteten Zeilen und der Index darauf. */
export interface PlantDatapoints {
  schema: PlantSchemaResponse;
  /** Alle Punkte der Anlage, ungefiltert und sortiert. */
  rows: DatapointRow[];
  /** Leaf-Id (klein) auf Zeile. Der Rueckweg vom gespeicherten Binding-Key. */
  byLeafId: Map<string, DatapointRow>;
}

/**
 * Laufende Anfragen je Anlage. Ein Eigenschaftspanel hat ein Dutzend
 * `BindingField`, und jedes braucht dasselbe Schema, um seinen Key in einen
 * lesbaren Namen aufzuloesen. Ohne diese Klammer waeren das ein Dutzend
 * Anfragen, ein Dutzend JSON-Parser-Laeufe ueber 4518 Punkte und ein Dutzend
 * Abflachungen — beim Anklicken jedes Widgets aufs Neue.
 *
 * Lebensdauer absichtlich kurz: ein Schema aendert sich waehrend einer
 * Editor-Sitzung selten, aber nicht nie (nebenan laeuft die Schema-Seite).
 * Eine Minute deckt das Oeffnen eines Widgets ab und nicht mehr.
 */
const schemaCache = new Map<string, { at: number; promise: Promise<PlantSchemaResponse> }>();
const SCHEMA_TTL_MS = 60_000;

/** Ableitung je Schema-Objekt. WeakMap, damit ein abgelaufenes Schema samt
 *  Ableitung verschwindet, ohne dass hier jemand aufraeumen muss. */
const derivedCache = new WeakMap<PlantSchemaResponse, PlantDatapoints>();

function loadPlantSchema(plantId: string): Promise<PlantSchemaResponse> {
  const hit = schemaCache.get(plantId);
  if (hit && Date.now() - hit.at < SCHEMA_TTL_MS) return hit.promise;
  // Kein AbortSignal: die Anfrage gehoert allen Feldern gemeinsam, und das
  // Aushaengen eines einzelnen Feldes darf sie den anderen nicht wegnehmen.
  const promise = api.get<PlantSchemaResponse>(`/api/plants/${plantId}/schema`)
    .catch((err: unknown) => {
      // Fehlschlaege nicht einfrieren, sonst haengt der Editor bis zum Neuladen
      // an einem einzigen schlechten Moment im Netz.
      schemaCache.delete(plantId);
      throw err;
    });
  schemaCache.set(plantId, { at: Date.now(), promise });
  return promise;
}

function derive(schema: PlantSchemaResponse): PlantDatapoints {
  const cached = derivedCache.get(schema);
  if (cached) return cached;
  const rows = flattenDatapointRows(schema, null);
  const built: PlantDatapoints = { schema, rows, byLeafId: indexRowsByLeafId(rows) };
  derivedCache.set(schema, built);
  return built;
}

/**
 * Holt Schema und Ableitung fuer eine Anlage. Von {@link DatapointPicker} und
 * von `hmi/editor/properties/BindingField.tsx` benutzt; dass beide dieselbe
 * Quelle nehmen, ist der Grund, warum ein gebundener Punkt im Feld und im
 * Waehler nie unterschiedlich heissen kann.
 */
export function usePlantDatapoints(plantId: string): AsyncState<PlantDatapoints> {
  return useAsync(() => loadPlantSchema(plantId).then(derive), [plantId]);
}
